import { GoogleCalendarEvent } from "../googleSyncApi";

/**
 * Deduplicates calendar events by selecting the most relevant instance when multiple versions exist.
 *
 * Algorithm:
 * 1. Group events by their base ID (the part before '_' in the event ID)
 * 2. For each group with multiple events, select one based on these priorities:
 *    a. Prefer events with originalStartTime (specific instances of recurring events)
 *    b. Prefer events where sequence=true (indicates part of a recurring series)
 *    c. If still tied, use the most recently updated event
 *
 * Example:
 * - Input: [baseEvent, baseEvent_R20241021, baseEvent_20241023Z]
 * - Output: Selects the specific instance (_20241023Z) over the recurring template
 */
export function deduplicateEvents(
  events: GoogleCalendarEvent[],
): GoogleCalendarEvent[] {
  const groupedEvents = events?.reduce(
    (acc, event) => {
      const baseId = event.id.split("_")[0];
      if (!acc[baseId]) {
        acc[baseId] = [];
      }
      acc[baseId].push(event);
      return acc;
    },
    {} as Record<string, GoogleCalendarEvent[]>,
  );

  const PREFER = -1;
  const DEFER = 1;

  return Object.values(groupedEvents).map((group) => {
    if (group.length === 1) {
      return group[0];
    }

    // Sort the group by priority rules
    const sortedGroup = group.sort((a, b) => {
      // Rule 1: Prefer events with originalStartTime (these are specific instances)
      if (a.originalStartTime && !b.originalStartTime) return PREFER;
      if (!a.originalStartTime && b.originalStartTime) return DEFER;

      // Rule 2: For events with sequence flag, prefer true
      if (a.sequence && !b.sequence) return PREFER;
      if (!a.sequence && b.sequence) return DEFER;

      // Rule 3: If still tied, use the most recently updated
      return new Date(b.updated).getTime() - new Date(a.updated).getTime();
    });

    return sortedGroup[0];
  });
}
